import { Layers, Link2, PanelLeftOpen, ScrollText, Settings, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { AccountMenu } from './AccountMenu';
import { settingsEntry, type NavArea, type NavEntry } from './adminNavigation';
import { CommandBar } from './CommandBar';
import { NavCount, NavEntryLink, ShellLogo } from './NavEntryLink';
import type { ActivePlace } from './navVisibility';
import { ObjectScopeMenu } from './ObjectScopeMenu';
import { countLabel, type NavCounts } from './useNavCounts';

interface SidebarRailProps {
  areas: NavArea[];
  platform: NavEntry[];
  place: ActivePlace;
  counts: NavCounts;
  objectScope: boolean;
  search: boolean;
  settings: boolean;
  onToggle: () => void;
  onLogout: () => void;
}

const PLATFORM_ICON: Record<string, LucideIcon> = {
  '/platform/workspaces': Layers,
  '/settings/integrations': Link2,
  '/system/audit': ScrollText,
};

const railItem = 'relative flex size-12 items-center justify-center rounded-lg';

/** Eingeklappte Seitenleiste: nur Zeichen, Zähler als Punkt mit Zahl, Namen als Tooltip. */
export function SidebarRail({
  areas,
  platform,
  place,
  counts,
  objectScope,
  search,
  settings,
  onToggle,
  onLogout,
}: SidebarRailProps) {
  const activeAreaId = place?.kind === 'area' ? place.area.id : null;
  return (
    <aside
      id="admin-navigation"
      aria-label="Hauptnavigation"
      className="border-hairline flex w-20 flex-none flex-col items-center gap-1.5 border-r bg-white py-[18px]"
    >
      <ShellLogo size={44} />
      <button
        type="button"
        onClick={onToggle}
        aria-label="Leiste ausklappen"
        title="Leiste ausklappen"
        className="border-hairline text-ink rounded-square my-1.5 flex h-9 w-11 items-center justify-center border bg-white"
      >
        <PanelLeftOpen size={18} aria-hidden />
      </button>
      {objectScope && <ObjectScopeMenu compact />}
      {search && <CommandBar variant="rail" />}
      <nav
        aria-label="Bereiche"
        className="flex min-h-0 flex-1 flex-col items-center gap-1.5 overflow-y-auto"
      >
        {areas.map((area) => {
          const active = area.id === activeAreaId;
          const countKey = area.count ?? area.entries.find((e) => e.count)?.count;
          const count = countKey ? counts[countKey] : undefined;
          return (
            <NavEntryLink
              key={area.id}
              entry={area.entries[0] as NavEntry}
              active={active}
              ariaLabel={countLabel(area.label, count)}
              title={countLabel(area.label, count)}
              className={cn(railItem, active ? 'bg-ink text-white' : 'text-ink hover:bg-card-alt')}
            >
              <area.icon size={20} aria-hidden />
              {count ? (
                <NavCount
                  value={count}
                  hot={countKey === 'revisionHolds'}
                  className="absolute right-0.5 top-0.5 h-[18px] min-w-[18px] border-2 border-white px-1 text-[10px]"
                />
              ) : null}
            </NavEntryLink>
          );
        })}
        {platform.length > 0 && <span className="bg-line my-1.5 h-px w-8" />}
        {platform.map((entry) => {
          const active = place?.kind === 'platform' && place.entry === entry;
          const Icon = PLATFORM_ICON[entry.to] ?? Layers;
          return (
            <NavEntryLink
              key={entry.to}
              entry={entry}
              active={active}
              ariaLabel={entry.label}
              title={entry.label}
              className={cn(
                railItem,
                active ? 'bg-ink text-white' : 'text-ink-soft hover:bg-card-alt',
              )}
            >
              <Icon size={18} aria-hidden />
            </NavEntryLink>
          );
        })}
      </nav>
      {settings && (
        <NavEntryLink
          entry={settingsEntry}
          active={place?.kind === 'settings'}
          ariaLabel="Einstellungen"
          title="Einstellungen"
          className={cn(
            railItem,
            place?.kind === 'settings' ? 'bg-ink text-white' : 'text-ink hover:bg-card-alt',
          )}
        >
          <Settings size={20} aria-hidden />
        </NavEntryLink>
      )}
      <AccountMenu variant="rail" onLogout={onLogout} />
    </aside>
  );
}
